import React, { useState } from 'react';
import type { FormikValues } from 'formik';
import { useFormikContext } from 'formik';
import { get } from 'lodash';

import Button from '~/components/core/Atomic/Buttons/Button';
import FsTooltip from '~/components/core/FsWrappers/FsTooltip/FsTooltip';
import { AddIcon } from '~/components/deprecatedMuiIcons';
import type { AdditionalInformationSection } from '~/components/types/incident-configuration-types';

import copies from '../../copies.json';
import { buildCategoryLimitOptions, usePolicyCategoryOptions } from '../../PolicyCategoriesOptions';

import AddCustomSectionDialog from './AddCustomSectionDialog';
import {
  ADDITIONAL_INFORMATION_MAX_SECTIONS,
  ADDITIONAL_INFORMATION_SECTIONS_PATH,
  countActiveSections,
} from './constants';

const TEXTS = copies.additional_information;

const AddSectionPanel = (): JSX.Element => {
  const { values, setFieldValue } = useFormikContext<FormikValues>();
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  // `?? []` not the lodash default: the field is nullable and `get` defaults only for `undefined`, so a
  // stored JSON `null` would otherwise crash the `.map`/`.length` below.
  const sections: AdditionalInformationSection[] = get(values, ADDITIONAL_INFORMATION_SECTIONS_PATH) ?? [];
  const existingKeys = sections.map((section) => section.id);
  const atCap = countActiveSections(sections) >= ADDITIONAL_INFORMATION_MAX_SECTIONS;

  const policyCategoryOptions = usePolicyCategoryOptions();
  const incidentCategories =
    (get(values, 'configuration.categories', []) as { id: string; desc: string }[])?.map((category) => ({
      id: category.id,
      value: category.id,
      label: category.desc,
    })) ?? [];
  // The new-section dialog offers the same grouped list as the section it creates.
  const categories = buildCategoryLimitOptions(incidentCategories, policyCategoryOptions);

  const closeDialog = () => setIsDialogOpen(false);

  const handleAddSection = (section: AdditionalInformationSection) => {
    setFieldValue(ADDITIONAL_INFORMATION_SECTIONS_PATH, [...sections, section]);
    closeDialog();
  };

  const addButton = (
    // MUI restores focus to whatever opened a dialog, which is what returns a keyboard user to their
    // place in the page -- so nothing here blurs the trigger on close. `focusRing` keeps that restored
    // focus from painting as a stuck selection.
    <Button variant="text" color="primary" focusRing onClick={() => setIsDialogOpen(true)} disabled={atCap}>
      <AddIcon />
      {TEXTS.add_section}
    </Button>
  );

  return (
    <div className="flex">
      {atCap ? (
        <FsTooltip
          arrow
          title={TEXTS.add_section_at_cap_tooltip.replace('{max}', String(ADDITIONAL_INFORMATION_MAX_SECTIONS))}
        >
          {/* span keeps the tooltip working over a disabled button (which swallows pointer events) */}
          <span>{addButton}</span>
        </FsTooltip>
      ) : (
        addButton
      )}
      <AddCustomSectionDialog
        isOpen={isDialogOpen}
        onClose={closeDialog}
        onSubmit={handleAddSection}
        existingKeys={existingKeys}
        categories={categories}
      />
    </div>
  );
};

export default AddSectionPanel;
